import EditOutlined from '@mui/icons-material/EditOutlined'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import type { ActivityNode, DependencyLink } from '../../api/network'
import { ProgressBar, StatusChipSmall } from '../NetworkPage'

/**
 * Everything known about one activity, in the order a site would ask it.
 *
 * <p>What is stopping it first, then how far it has got, then what it is waiting on in
 * each of the four streams. The schedule arithmetic is last: it matters to a planner, but
 * the drawing that has not arrived matters to everyone.</p>
 */
export function ActivityDetail({
  activity,
  activities,
  dependencies,
  onEdit,
}: {
  activity: ActivityNode | null
  activities: ActivityNode[]
  dependencies: DependencyLink[]
  onEdit: () => void
}) {
  if (!activity) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography color="text.secondary">
          Pick an activity on the network to see what it needs, how far it has got, and what
          is holding it up.
        </Typography>
      </Paper>
    )
  }

  const predecessors = dependencies.filter((d) => d.successorId === activity.id)
  const successors = dependencies.filter((d) => d.predecessorId === activity.id)
  const codeOf = (id: string) => activities.find((a) => a.id === id)?.code ?? '?'

  return (
    <Paper variant="outlined" sx={{ p: 2, alignSelf: 'start', position: 'sticky', top: 88 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" sx={{ fontFamily: 'monospace' }} color="text.secondary">
            {activity.code}
          </Typography>
          <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
            {activity.name}
          </Typography>
        </Box>
        <Button size="small" startIcon={<EditOutlined />} onClick={onEdit}>
          Edit
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <StatusChipSmall status={activity.status} />
        {activity.critical && (
          <Chip size="small" label="Critical path" sx={{ bgcolor: '#fbe0de', color: '#8c1d18', fontWeight: 700 }} />
        )}
        {activity.milestone && <Chip size="small" label="Milestone" />}
        {!activity.critical && (
          <Chip size="small" variant="outlined" label={`${activity.totalFloat}d float`} />
        )}
      </Box>

      {activity.blockers.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {activity.blockers.map((blocker) => (
              <li key={blocker}>{blocker}</li>
            ))}
          </Box>
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary">
        Progress, from recorded quantities
      </Typography>
      <Box sx={{ mb: 2 }}>
        <ProgressBar percent={activity.percentComplete} behind={activity.behindSchedule} />
      </Box>

      <Section title="Work completion">
        {activity.workItems.length === 0 ? (
          <Muted>No BOQ lines linked, so progress cannot be measured.</Muted>
        ) : (
          activity.workItems.map((item) => (
            <Row
              key={item.workItemId}
              left={<span style={{ fontFamily: 'monospace' }}>{item.code}</span>}
              right={`${fmt(item.completedQuantity)} / ${fmt(item.totalPlannedQuantity)} ${item.uomCode ?? ''}`}
              detail={item.description}
            />
          ))
        )}
      </Section>

      <Section title="Drawings">
        {activity.drawings.length === 0 ? (
          <Muted>None linked.</Muted>
        ) : (
          activity.drawings.map((drawing) => (
            <Row
              key={drawing.drawingId}
              left={<span style={{ fontFamily: 'monospace' }}>{drawing.drawingNumber}</span>}
              right={
                drawing.received ? (
                  <Chip size="small" label="received" sx={{ bgcolor: '#dcf2e6', color: '#0b5d3b' }} />
                ) : (
                  <Chip
                    size="small"
                    label={drawing.daysPending != null ? `${drawing.daysPending}d pending` : 'pending'}
                    sx={{ bgcolor: '#fbe0de', color: '#8c1d18', fontWeight: 700 }}
                  />
                )
              }
              detail={drawing.title}
            />
          ))
        )}
      </Section>

      <Section title="Material received">
        {activity.materials.length === 0 ? (
          <Muted>None linked.</Muted>
        ) : (
          <>
            {activity.materials.map((material) => (
              <Row
                key={material.materialId}
                left={material.materialName}
                right={
                  material.shortageQuantity > 0 ? (
                    <span style={{ color: '#b3261e', fontWeight: 700 }}>
                      short {fmt(material.shortageQuantity)} {material.uomCode ?? ''}
                    </span>
                  ) : (
                    <span style={{ color: '#0b5d3b' }}>enough</span>
                  )
                }
                detail={`${fmt(material.receivedQuantity)} received of ${fmt(material.requiredQuantity)} needed`}
              />
            ))}
            <Muted>Counted from deliveries accepted at this site. Consumption is not tracked yet.</Muted>
          </>
        )}
      </Section>

      <Section title="Labour worked">
        {activity.labour.length === 0 ? (
          <Muted>None linked.</Muted>
        ) : (
          activity.labour.map((trade) => (
            <Row
              key={trade.labourCategoryId}
              left={trade.labourCategoryName}
              right={
                (trade.shortfall ?? 0) > 0 ? (
                  <span style={{ color: '#b3261e', fontWeight: 700 }}>short {trade.shortfall}</span>
                ) : (
                  <span style={{ color: '#0b5d3b' }}>{trade.recentWorkers ?? 0} on site</span>
                )
              }
              detail={
                trade.recentOn
                  ? `${trade.recentWorkers ?? 0} recorded on ${formatDate(trade.recentOn)}, ${trade.requiredWorkers ?? 0} needed`
                  : `None recorded yet, ${trade.requiredWorkers ?? 0} needed`
              }
            />
          ))
        )}
      </Section>

      <Divider sx={{ my: 2 }} />

      <Section title="Schedule">
        <Row left="Scheduled" right={`${formatDate(activity.scheduledStart)} → ${formatDate(activity.scheduledFinish)}`} />
        <Row left="Duration" right={activity.milestone ? 'Milestone' : `${activity.durationDays} days`} />
        <Row left="Earliest start / finish" right={`day ${activity.earliestStart} / ${activity.earliestFinish}`} />
        <Row left="Latest start / finish" right={`day ${activity.latestStart} / ${activity.latestFinish}`} />
        <Row left="Total float" right={`${activity.totalFloat} days`} />
      </Section>

      <Section title="Depends on">
        {predecessors.length === 0 ? (
          <Muted>Nothing — this can start as soon as the site does.</Muted>
        ) : (
          predecessors.map((link) => (
            <Row
              key={link.id}
              left={<span style={{ fontFamily: 'monospace' }}>{codeOf(link.predecessorId)}</span>}
              right={`${link.dependencyType}${link.lagDays ? ` ${link.lagDays > 0 ? '+' : ''}${link.lagDays}d` : ''}`}
            />
          ))
        )}
      </Section>

      <Section title="Holds up">
        {successors.length === 0 ? (
          <Muted>Nothing depends on this.</Muted>
        ) : (
          successors.map((link) => (
            <Row
              key={link.id}
              left={<span style={{ fontFamily: 'monospace' }}>{codeOf(link.successorId)}</span>}
              right={`${link.dependencyType}${link.lagDays ? ` ${link.lagDays > 0 ? '+' : ''}${link.lagDays}d` : ''}`}
            />
          ))
        )}
      </Section>
    </Paper>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  )
}

function Row({
  left,
  right,
  detail,
}: {
  left: React.ReactNode
  right?: React.ReactNode
  detail?: string
}) {
  return (
    <Box sx={{ mb: 0.75 }}>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ flex: 1 }}>
          {left}
        </Typography>
        <Typography variant="body2" component="div">
          {right}
        </Typography>
      </Box>
      {detail && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {detail}
        </Typography>
      )}
    </Box>
  )
}

function Muted({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {children}
    </Typography>
  )
}

function fmt(value?: number): string {
  if (value == null) return '-'
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 3 })
}

function formatDate(value?: string): string {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' })
}
